<script setup lang="ts">
import type { Schemas } from "#shopware";
import { getPrefix } from "./i18n/src/helpers/prefix";
const config = useRuntimeConfig();
import { useWindowSize } from '@vueuse/core';
import { useScrollLock } from '~/composables/useScrollLock';

import { useMenuStore } from "@/stores/menu";
import { useSearchStore } from "@/stores/search";
import { useModalStore } from "@/stores/modal";
import { useFlyoutStore } from "@/stores/flyout";
import { useSidebarStore } from "@/stores/sidebar";
import { useClearVariantStore } from "~/composables/useClearVariantStore";
import { useHistoryNavigation } from '~/composables/useHistoryNavigation'


const menuStore = useMenuStore();
const searchStore = useSearchStore();
const modalStore = useModalStore();
const flyoutStore = useFlyoutStore();
const sidebarStore = useSidebarStore();

// Initialize the clear variant store watcher
useClearVariantStore();

// Initialize scroll lock
const { isLocked, disableScroll, enableScroll } = useScrollLock();
const router = useRouter();
const route = useRoute();

// Scroll-related data
const lastScrollTop = ref(0);
const scrollThreshold = 200;
const ticking = ref(false);

// Create a computed property for the body class
const { width } = useWindowSize();

// Add loading state
const isRouteLoading = ref(false);

// Initialize history navigation tracking
const { provideHistoryNavigation, isHistoryBack } = useHistoryNavigation()
provideHistoryNavigation()

// Watch for route changes
router.beforeEach((to: any, from: any) => {
  // Only show loading indicator if navigating to a different route
  if (to.path !== from.path) {
    isRouteLoading.value = true;
    menuStore.isMainMenuOpen = false;
    menuStore.isMobileMenuOpen = false;
    sidebarStore.closeSidebar();
  }
});

// Use Nuxt's built-in loading state
const nuxtApp = useNuxtApp();
nuxtApp.hook('page:start', () => {
  isRouteLoading.value = true;
});

nuxtApp.hook('page:finish', () => {
  // Only scroll if not a history back navigation and there is no hash anchor
  // Delay scroll to ensure page content has rendered
  if (!isHistoryBack.value && !route.hash) {
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 50);
  }
  if (process.client) {
    // No hash: standard scroll-to-top (unless coming from history back)
    // With hash: scroll to anchor with a small offset from the top
    if (route.hash) {
      const id = route.hash.substring(1);
      let attempts = 0;
      const maxAttempts = 10;
      const offset = 200; // pixels above the target

      const tryScrollToHash = () => {
        const target = document.getElementById(id);
        if (target) {
          const rect = target.getBoundingClientRect();
          const top = rect.top + window.scrollY - offset;

          window.scrollTo({
            top,
            behavior: "smooth",
          });
          return;
        }

        // If element not yet in DOM (lazy components etc.), retry a few times
        if (attempts < maxAttempts) {
          attempts += 1;
          setTimeout(tryScrollToHash, 50);
        }
      };

      setTimeout(tryScrollToHash, 50);
    }
  }
  setTimeout(() => {
    isRouteLoading.value = false;
  }, 300);
});

const bodyClass = computed(() => {
  const classes = ["flex", "flex-col", "min-h-[100dvh]"];
  if (menuStore.isMobileMenuOpen) classes.push("mobile-menu-open");
  if (menuStore.isMainMenuOpen) classes.push("menu-open");
  if (searchStore.isSearchSectionOpen) classes.push("search-is-open");
  if (searchStore.isSearchSuggestVisible) classes.push("search-suggest-open");
  if (modalStore.isModalOpen) classes.push("modal-open");
  if (flyoutStore.isFlyoutOpen) classes.push("flyout-open");
  if (sidebarStore.isSidebarOpen) classes.push("sidebar-open");
  if (isRouteLoading.value) classes.push("page-loading");

  return classes.join(" ");
});

// Watch for changes in bodyClass and update the body element
watch(bodyClass, (newClass) => {
  if (process.client) {
    // Save the sticky classes state before updating
    const headerIsSticky = document.body.classList.contains('header-is-sticky');
    const mobileNavIsSticky = document.body.classList.contains('mobile-nav-is-sticky');
    const navHeaderHidden = document.body.classList.contains('nav-header-hidden');
    
    // Update the base classes
    document.body.className = newClass;
    
    // Restore sticky classes if they were present
    if (headerIsSticky) document.body.classList.add('header-is-sticky');
    if (mobileNavIsSticky) document.body.classList.add('mobile-nav-is-sticky');
    if (navHeaderHidden) document.body.classList.add('nav-header-hidden');
    
    // List of classes that should lock scroll
    const scrollLockClasses = [
      "mobile-menu-open",
      "menu-open",
      "page-loading",
      "mobile-menu-open",
      "modal-open",
      "flyout-open",
      "sidebar-open"
      // Note: We don't add "search-suggest-open" here as we don't want to lock scroll when search suggestions are visible
    ];
    // If any of these classes are present, disable scroll; otherwise, enable scroll
    const shouldLock = scrollLockClasses.some(cls => document.body.classList.contains(cls));
    if (shouldLock) {
      disableScroll();
    } else {
      enableScroll();
    }
  }
}, { immediate: true });

/**
 * Init breadcrumbs context
 */
useBreadcrumbs();

useHead(() => {
  const path = router.currentRoute.value.path;
  const isSearchPage = path.includes("/search");

  return {
    title: isSearchPage
      ? undefined
      : "Schlafsofa & Schlafcouch Online-Shop ♥ | mysofabed.de",
    meta: [
      {
        name: "robots",
        content: isSearchPage ? "noindex, follow" : "index, follow",
        tagPriority: -8,
      },
    ],
    htmlAttrs: {
      lang: "en",
    },
  };
});

const { apiClient } = useShopwareContext();
const sessionContextData = ref<Schemas["SalesChannelContext"]>();
const contextResponse = await apiClient.invoke("readContext get /context");
sessionContextData.value = contextResponse.data;

if (config?.public?.broadcasting) {
  useBroadcastChannelSync();
}

// If you enable runtimeConfig.shopware.useUserContextInSSR, then you can use this code to share session between server and client.
// const { data: sessionContextData } = await useAsyncData(
//   "sessionContext",
//   async () => {
//     return await getSessionContext(apiInstance);
//   }
// );

// read the locale/lang code from accept-language header (i.e. en, en-GB or de-DE)
// and set configuration for price formatting globally
const headers = useRequestHeaders();
// Extract the first (with highest priority order) locale or lang code from accept-language header
// for example: "en-US;q=0.7,en;q=0.3" will return "en-US"
const localeFromHeader = headers?.["accept-language"]
  ?.split(",")
  ?.map(
    (languageConfig) => languageConfig.match(/^([a-z]{2}(?:-[A-Z]{2})?)/)?.[0],
  )
  .find(Boolean);

usePrice({
  currencyCode: sessionContextData.value?.currency?.isoCode || "",
  localeCode: localeFromHeader,
});

useSessionContext(sessionContextData.value);

const { getWishlistProducts } = useWishlist();
const { refreshCart } = useCart();

useNotifications();
useAddress();

const { locale, availableLocales, defaultLocale, localeProperties, messages } =
  useI18n();
const {
  getAvailableLanguages,
  getLanguageCodeFromId,
  getLanguageIdFromCode,
  changeLanguage,
  languages: storeLanguages,
} = useInternationalization();
const { languageIdChain, refreshSessionContext } = useSessionContext();

const { data: languages } = await useAsyncData("languages", async () => {
  return await getAvailableLanguages();
});
let languageToChangeId: string | null = null;

if (languages.value?.elements.length && router.currentRoute.value.name) {
  storeLanguages.value = languages.value?.elements;
  // Prefix from url
  const prefix = getPrefix(
    availableLocales,
    router.currentRoute.value.name as string,
    defaultLocale,
  );

  provide("cmsTranslations", messages.value[prefix || defaultLocale] ?? {});

  // Language set on the backend side
  if (localeProperties.value.localeId) {
    if (languageIdChain.value !== localeProperties.value.localeId) {
      languageToChangeId = localeProperties.value.localeId;
    }
  } else {
    const sessionLanguage = getLanguageCodeFromId(languageIdChain.value);

    // If languages are not the same, set one from prefix
    if (sessionLanguage !== prefix) {
      languageToChangeId = getLanguageIdFromCode(
        prefix ? prefix : defaultLocale,
      );
    }
  }

  if (languageToChangeId) {
    await changeLanguage(languageToChangeId);
    await refreshSessionContext();
  }

  locale.value = prefix ? prefix : defaultLocale;
  // Set prefix from CMS components
  provide("urlPrefix", prefix);
}

if (import.meta.client) {
  // Clear recovery flag when page loaded successfully (not on error page)
  const nuxtError = useError();
  if (!nuxtError.value) {
    clearPageRecoveryAttempt(route.fullPath);
  }

  // getting the wishlist products should not block SSR
  if (!(router.currentRoute.value.name as string).includes("wishlist")) {
    getWishlistProducts(); // initial page loading
  }
}

let headerStickyTriggerY = 0;
let mobileNavStickyTriggerY = 0;
let headerStickyCooldown = false;
let mobileNavStickyCooldown = false;

const STICKY_COOLDOWN_MS = 350;

function updateStickyClass(
  className: string,
  triggerY: number,
  isOnCooldown: () => boolean,
  setCooldown: (value: boolean) => void,
  scrollY: number,
) {
  if (isOnCooldown()) return;
  const isSticky = document.body.classList.contains(className);
  const shouldBeSticky = scrollY > triggerY;
  if (isSticky === shouldBeSticky) return;
  document.body.classList.toggle(className, shouldBeSticky);
  setCooldown(true);
  setTimeout(() => setCooldown(false), STICKY_COOLDOWN_MS);
}

// Scroll handler
// TODO Change the document selector
function handleScroll() {
  const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;

  updateStickyClass(
    "header-is-sticky",
    headerStickyTriggerY,
    () => headerStickyCooldown,
    (v) => { headerStickyCooldown = v; },
    currentScrollTop,
  );
  updateStickyClass(
    "mobile-nav-is-sticky",
    mobileNavStickyTriggerY,
    () => mobileNavStickyCooldown,
    (v) => { mobileNavStickyCooldown = v; },
    currentScrollTop,
  );

  // Only apply nav-header-hidden behavior on screens that are not - (height < 1100px && width < 768)
  if (window.innerHeight >= 1024 || window.innerWidth <= 768) {
    return;
  }

  if (!ticking.value) {
    window.requestAnimationFrame(() => {
      if (currentScrollTop > scrollThreshold) {
        if (currentScrollTop > lastScrollTop.value) {
          document.body.classList.add('nav-header-hidden');
        } else {
          document.body.classList.remove('nav-header-hidden');
        }
      } else {
        document.body.classList.remove('nav-header-hidden');
      }
      lastScrollTop.value = currentScrollTop;
      ticking.value = false;
    });
    ticking.value = true;
  }
} 



// Resize handler for layout (e.g. nav-header visibility)
function handleResize() {
    if (window.innerHeight >= 1024 || window.innerWidth <= 768) {
      document.body.classList.remove('nav-header-hidden');
    }
    // Do not reload the accessibility widget on resize: it causes multiple script
    // injections and "AccessibleWebWidget has already been declared" errors.
  }

onMounted(() => {

  window.addEventListener('online', () => {
    window.location.reload();
  })
  
  refreshCart();
  window.addEventListener("scroll", handleScroll, { passive: true });
  window.addEventListener("resize", handleResize, { passive: true });

  const header = document.querySelector<HTMLElement>("header");
  const mobileNav = document.querySelector<HTMLElement>(".category-nav .nav-header");

  if (header) {
    const sentinel = document.createElement("div");
    sentinel.classList.add("header-is-sticky-sentinel");
    header.parentElement?.insertBefore(sentinel, header);
    headerStickyTriggerY = Math.round(sentinel.getBoundingClientRect().top + window.scrollY);
  }
  if (mobileNav) {
    const sentinel = document.createElement("div");
    sentinel.classList.add("mobile-nav-is-sticky-sentinel");
    mobileNav.parentElement?.insertBefore(sentinel, mobileNav);
    mobileNavStickyTriggerY = Math.round(sentinel.getBoundingClientRect().top + window.scrollY);
  }
  handleScroll();

  // Load accessibility widget script after page mount
  loadAccessibilityWidget();
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", handleScroll);
  window.removeEventListener("resize", handleResize);
});

// Prevent adding the accessibility widget script more than once (avoids "redeclaration of AccessibleWebWidget" when script runs twice)
let accessibilityWidgetScriptAdded = false;

// Function to load accessibility widget script dynamically
function loadAccessibilityWidget(force = false) {
  if (process.client) {
    const existingScript = document.querySelector('script[src*="accessible-web-widget"]');
    if (!force && (existingScript || accessibilityWidgetScriptAdded)) {
      return;
    }
    if (force && existingScript) {
      existingScript.remove();
      accessibilityWidgetScriptAdded = false;
    }
    accessibilityWidgetScriptAdded = true;

    const script = document.createElement('script');
    script.src = '/accessible-web-widget.min.js';
    script.defer = true;
    script.async = true;

    if (force) {
      setTimeout(() => {
        document.head.appendChild(script);
      }, 1000);
    } else {
      document.head.appendChild(script);
    }
  }
}
</script>

<template>
  <div class="app-container">
    <!-- Main loading indicator -->
    <NuxtLoadingIndicator 
      :height="3"
      :duration="3000"
      :throttle="200"
      color="#6F6D52"
      class="!z-[9999]"
    />
    <ClientOnly>
      <UnsupportedIPadOSBanner />
    </ClientOnly>
    <NuxtLayout>
      <!-- Wrap NuxtPage with a relative container -->
      <div class="relative">
        <!-- Loading overlay with generic loader -->
        <Transition
          enter-active-class="transition-opacity duration-300"
          enter-from-class="opacity-0"
          enter-to-class="opacity-100"
          leave-active-class="transition-opacity duration-300" 
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <div 
            v-if="isRouteLoading"
            class="loading-overlay absolute inset-0 z-[9998] bg-white flex items-start justify-center"
          >
            <div class="flex flex-col items-center space-y-6 fixed top-[50%] -translate-y-1/2">
              <!-- Modern loading animation with multiple elements -->
              <div class="relative">
                <!-- Outer ring -->
                <div class="w-16 h-16 border-2 border-gray-200 rounded-full animate-pulse"></div>
                <!-- Inner spinning ring -->
                <div class="absolute top-0 left-0 w-16 h-16 border-2 border-transparent border-t-[#6F6D52] rounded-full animate-spin"></div>
              </div>
            </div>
          </div>
        </Transition>
        <NuxtPage />
      </div>
    </NuxtLayout>
  </div>
</template>
